import type { PageHelp } from '../../help/types';

/** Page guide for `/settings/api-tokens` (spec §9.5, §5.7). */
export const apiTokensHelp: PageHelp = {
  title: 'API tokens',
  intro:
    'Credentials for pipelines: reading a published bundle, publishing a release, reporting missing keys. A token carries scopes rather than a role, and it can never sign in to this application.',
  sections: ['Tokens in use', 'New token', 'Copying the secret'],
  contexts: {
    'token-dialog': 'Click “New token” at the top right.',
    'token-created': 'Fill in the “New API token” dialog and click “Create”.',
  },
  steps: [
    // --- Tokens in use ---------------------------------------------------------------------
    {
      selector: '[data-help="tokens-list"]',
      title: 'Review the tokens',
      body: 'Every token issued so far, newest first, with who created it and when. A revoked token stays in the list, greyed out, so you can still see what it was for.',
      section: 'Tokens in use',
      i18n: {
        'de-AT': {
          title: 'Tokens durchsehen',
          body: 'Jeder bisher ausgestellte Token, der neueste zuerst, mit Ersteller und Datum. Ein widerrufener Token bleibt ausgegraut in der Liste, damit Sie weiterhin sehen, wofür er war.',
        },
        'sr-Latn-RS': {
          title: 'Pregledajte tokene',
          body: 'Svaki do sada izdat token, od najnovijeg, sa autorom i datumom. Opozvan token ostaje na listi, zasivljen, pa i dalje vidite čemu je služio.',
        },
      },
    },
    {
      selector: '[data-help="tokens-scopes"]',
      title: 'Read what a token may do',
      body: 'The scopes decide which machine routes accept the token: `export:read` reads bundles, `publish:write` publishes a release and `missing-keys:write` reports keys a host app is missing and `imports:write` imports a translation file into the project (`POST /v1/projects/{code}/imports`, as draft, never over reviewed values). Any route its scopes do not cover refuses the token.',
      section: 'Tokens in use',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Lesen, was ein Token darf',
          body: 'Die Scopes bestimmen, welche Maschinen-Routen den Token annehmen: `export:read` liest Bundles, `publish:write` veröffentlicht ein Release und `missing-keys:write` meldet Keys, die einer Host-App fehlen, und `imports:write` importiert eine Übersetzungsdatei in das Projekt (`POST /v1/projects/{code}/imports`, als Entwurf, nie über geprüfte Werte). Jede Route, die seine Scopes nicht abdecken, lehnt den Token ab.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte šta token sme',
          body: 'Scope-ovi određuju koje mašinske rute prihvataju token: `export:read` čita bundle-ove, `publish:write` objavljuje release, a `missing-keys:write` prijavljuje ključeve koji nedostaju host aplikaciji, a `imports:write` uvozi fajl sa prevodima u projekat (`POST /v1/projects/{code}/imports`, kao draft, nikad preko pregledanih vrednosti). Svaka ruta koju njegovi scope-ovi ne pokrivaju odbija token.',
        },
      },
    },
    {
      selector: '[data-help="tokens-project"]',
      title: 'See which project a token is limited to',
      body: 'A token narrowed to one project is refused on every other project; “every project” means it is not narrowed. Narrow a token unless its pipeline really serves several projects.',
      section: 'Tokens in use',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Sehen, auf welches Projekt ein Token beschränkt ist',
          body: 'Ein auf ein Projekt beschränkter Token wird bei jedem anderen Projekt abgelehnt; „every project“ heißt, er ist nicht beschränkt. Beschränken Sie einen Token, außer seine Pipeline bedient wirklich mehrere Projekte.',
        },
        'sr-Latn-RS': {
          title: 'Pogledajte na koji je projekat token ograničen',
          body: 'Token ograničen na jedan projekat odbija se na svakom drugom projektu; „every project“ znači da nije ograničen. Ograničite token, osim ako njegov pipeline zaista služi više projekata.',
        },
      },
    },
    {
      selector: '[data-help="tokens-last-used"]',
      title: 'Find tokens nobody uses',
      body: 'Updated every time the token authenticates. It is the only way to tell a token a pipeline still needs from one that has been forgotten: an amber “never” on an old token means it is safe to revoke.',
      section: 'Tokens in use',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ungenutzte Tokens finden',
          body: 'Wird bei jeder Authentifizierung des Tokens aktualisiert. Nur so unterscheiden Sie einen Token, den eine Pipeline noch braucht, von einem vergessenen: Ein gelbes „never“ bei einem alten Token heißt, dass Sie ihn gefahrlos widerrufen können.',
        },
        'sr-Latn-RS': {
          title: 'Pronađite tokene koje niko ne koristi',
          body: 'Ažurira se pri svakoj autentifikaciji tokena. Jedino tako razlikujete token koji je pipeline-u još potreban od zaboravljenog: žuto „never“ kod starog tokena znači da ga možete bezbedno opozvati.',
        },
      },
    },
    {
      selector: '[data-help="tokens-expires"]',
      title: 'Check the expiry date',
      body: 'From this date on the token is refused like an unknown one. A token lives at most 365 days, so create a replacement and update the pipeline before the date arrives.',
      section: 'Tokens in use',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ablaufdatum prüfen',
          body: 'Ab diesem Datum wird der Token wie ein unbekannter abgelehnt. Ein Token gilt höchstens 365 Tage – legen Sie rechtzeitig einen Ersatz an und stellen Sie die Pipeline vor dem Datum um.',
        },
        'sr-Latn-RS': {
          title: 'Proverite datum isteka',
          body: 'Od tog datuma token se odbija kao nepoznat. Token važi najviše 365 dana, pa na vreme napravite zamenu i ažurirajte pipeline pre tog datuma.',
        },
      },
    },
    {
      selector: '[data-help="tokens-expired"]',
      title: 'Recognise an expired token',
      body: 'A token past its expiry date is marked “expired” and greyed out like a revoked one, because every request with it already gets 401. Create a replacement for the pipeline that used it, then revoke the old one to tidy up the list.',
      section: 'Tokens in use',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Abgelaufenen Token erkennen',
          body: 'Ein Token nach seinem Ablaufdatum ist mit „expired“ markiert und wie ein widerrufener ausgegraut, weil jede Anfrage damit bereits 401 bekommt. Legen Sie für die Pipeline, die ihn verwendet hat, einen Ersatz an und widerrufen Sie dann den alten, um die Liste aufzuräumen.',
        },
        'sr-Latn-RS': {
          title: 'Prepoznajte istekao token',
          body: 'Token kome je prošao datum isteka označen je sa „expired“ i zasivljen kao opozvan, jer svaki zahtev sa njim već dobija 401. Napravite zamenu za pipeline koji ga je koristio, pa opozovite stari da biste raspremili listu.',
        },
      },
    },
    {
      selector: '[data-help="tokens-revoke"]',
      title: 'Revoke a token',
      body: 'After you confirm, anything still using the token gets 401 on its next request. A revoked token cannot be restored; create a new one instead.',
      section: 'Tokens in use',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Token widerrufen',
          body: 'Nach Ihrer Bestätigung bekommt alles, was den Token noch verwendet, bei der nächsten Anfrage 401. Ein widerrufener Token lässt sich nicht wiederherstellen; legen Sie stattdessen einen neuen an.',
        },
        'sr-Latn-RS': {
          title: 'Opozovite token',
          body: 'Kada potvrdite, sve što još koristi token dobija 401 na sledećem zahtevu. Opozvan token ne može da se vrati; umesto toga napravite novi.',
        },
      },
    },

    // --- New token -------------------------------------------------------------------------
    {
      selector: '[data-help="tokens-create"]',
      title: 'Create a token',
      body: 'Opens the “New API token” dialog. A token is for a pipeline, not for a person: give every pipeline its own, so one can be revoked without breaking the others.',
      section: 'New token',
      i18n: {
        'de-AT': {
          title: 'Token anlegen',
          body: 'Öffnet den Dialog „New API token“. Ein Token ist für eine Pipeline, nicht für eine Person: Geben Sie jeder Pipeline ihren eigenen, damit einer widerrufen werden kann, ohne die anderen zu stören.',
        },
        'sr-Latn-RS': {
          title: 'Napravite token',
          body: 'Otvara dijalog „New API token“. Token je za pipeline, ne za osobu: svakom pipeline-u dajte poseban, da biste jedan mogli da opozovete bez kvarenja ostalih.',
        },
      },
    },
    {
      selector: '[data-help="tokens-new-name"]',
      title: 'Name the token after what uses it',
      body: 'Required. Write what uses it, such as “hbh CI publish”: the secret can never be read again, so the name is the only clue you will have later.',
      section: 'New token',
      context: 'token-dialog',
      i18n: {
        'de-AT': {
          title: 'Token nach seinem Verwender benennen',
          body: 'Pflichtfeld. Schreiben Sie, was ihn verwendet, etwa „hbh CI publish“: Das Secret lässt sich nie wieder lesen, der Name ist also später Ihr einziger Anhaltspunkt.',
        },
        'sr-Latn-RS': {
          title: 'Nazovite token po onome što ga koristi',
          body: 'Obavezno. Napišite šta ga koristi, na primer „hbh CI publish“: tajna vrednost se više nikad ne može pročitati, pa je ime jedini trag koji ćete kasnije imati.',
        },
      },
    },
    {
      selector: '[data-help="tokens-new-scopes"]',
      title: 'Choose the scopes',
      body: 'At least one is required, and `export:read` is preselected. Give a pipeline only the scopes it needs — a token that only downloads bundles has no reason to publish.',
      section: 'New token',
      context: 'token-dialog',
      i18n: {
        'de-AT': {
          title: 'Scopes wählen',
          body: 'Mindestens einer ist nötig, `export:read` ist vorausgewählt. Geben Sie einer Pipeline nur die Scopes, die sie braucht – ein Token, der nur Bundles lädt, muss nicht veröffentlichen können.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite scope-ove',
          body: 'Potreban je bar jedan, a `export:read` je unapred izabran. Dajte pipeline-u samo scope-ove koji mu trebaju – token koji samo preuzima bundle-ove nema razloga da objavljuje.',
        },
      },
    },
    {
      selector: '[data-help="tokens-new-project"]',
      title: 'Limit the token to one project',
      body: '“Every project” is the default. Pick one project unless the token genuinely needs all of them, so a leaked secret reaches as little as possible.',
      section: 'New token',
      context: 'token-dialog',
      i18n: {
        'de-AT': {
          title: 'Token auf ein Projekt beschränken',
          body: 'Standard ist „Every project“. Wählen Sie ein Projekt, außer der Token braucht wirklich alle – so erreicht ein geleaktes Secret so wenig wie möglich.',
        },
        'sr-Latn-RS': {
          title: 'Ograničite token na jedan projekat',
          body: 'Podrazumevano je „Every project“. Izaberite jedan projekat, osim ako tokenu zaista trebaju svi, da procurela tajna vrednost dosegne što manje.',
        },
      },
    },
    {
      selector: '[data-help="tokens-new-expiry"]',
      title: 'Set the lifetime',
      body: 'Between 1 and 365 days, 180 by default; the field accepts digits only. A token that never expires is not allowed.',
      section: 'New token',
      context: 'token-dialog',
      i18n: {
        'de-AT': {
          title: 'Laufzeit festlegen',
          body: 'Zwischen 1 und 365 Tagen, standardmäßig 180; das Feld nimmt nur Ziffern an. Ein Token, der nie abläuft, ist nicht erlaubt.',
        },
        'sr-Latn-RS': {
          title: 'Podesite trajanje',
          body: 'Između 1 i 365 dana, podrazumevano 180; polje prima samo cifre. Token koji nikad ne ističe nije dozvoljen.',
        },
      },
    },
    {
      selector: '[data-help="tokens-new-create"]',
      title: 'Create the token',
      body: 'Enabled once a name, at least one scope and a valid lifetime are set. The dialog then shows the secret, and that is the only time you will see it.',
      section: 'New token',
      context: 'token-dialog',
      i18n: {
        'de-AT': {
          title: 'Token erstellen',
          body: 'Aktiv, sobald Name, mindestens ein Scope und eine gültige Laufzeit gesetzt sind. Danach zeigt der Dialog das Secret – das einzige Mal, dass Sie es sehen.',
        },
        'sr-Latn-RS': {
          title: 'Napravite token',
          body: 'Aktivno kada su uneti ime, bar jedan scope i ispravno trajanje. Dijalog zatim prikazuje tajnu vrednost, i to je jedini put da je vidite.',
        },
      },
    },

    // --- Copying the secret ----------------------------------------------------------------
    {
      selector: '[data-help="tokens-secret"]',
      title: 'Copy the secret now',
      body: 'This is the only time the secret can be read: only its hash is stored. If you close the dialog without copying it, revoke the token and create a new one.',
      section: 'Copying the secret',
      context: 'token-created',
      i18n: {
        'de-AT': {
          title: 'Secret jetzt kopieren',
          body: 'Nur jetzt lässt sich das Secret lesen: Gespeichert wird nur sein Hash. Schließen Sie den Dialog, ohne es zu kopieren, widerrufen Sie den Token und legen Sie einen neuen an.',
        },
        'sr-Latn-RS': {
          title: 'Kopirajte tajnu vrednost odmah',
          body: 'Samo sada tajna vrednost može da se pročita: čuva se samo njen hash. Ako zatvorite dijalog bez kopiranja, opozovite token i napravite novi.',
        },
      },
    },
    {
      selector: '[data-help="tokens-copy"]',
      title: 'Copy it to the clipboard',
      body: 'Copies the secret so you can paste it into the pipeline’s secret store; “Copied” appears only once the browser confirms it. If the browser refuses, an error says so — select the secret and copy it by hand. The pipeline sends it in the header `Authorization: ApiToken <secret>`.',
      section: 'Copying the secret',
      context: 'token-created',
      i18n: {
        'de-AT': {
          title: 'In die Zwischenablage kopieren',
          body: 'Kopiert das Secret, damit Sie es in den Secret-Speicher der Pipeline einfügen können; „Copied“ erscheint erst, wenn der Browser es bestätigt. Verweigert der Browser das Kopieren, meldet ein Fehler das – markieren Sie das Secret dann und kopieren Sie es von Hand. Die Pipeline sendet es im Header `Authorization: ApiToken <secret>`.',
        },
        'sr-Latn-RS': {
          title: 'Kopirajte je u clipboard',
          body: 'Kopira tajnu vrednost da biste je nalepili u skladište tajni pipeline-a; „Copied“ se pojavljuje tek kada pregledač to potvrdi. Ako pregledač odbije, greška to javlja – tada označite tajnu vrednost i kopirajte je ručno. Pipeline je šalje u zaglavlju `Authorization: ApiToken <secret>`.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'API-Tokens',
      intro:
        'Zugangsdaten für Pipelines: ein veröffentlichtes Bundle lesen, ein Release veröffentlichen, fehlende Keys melden. Ein Token trägt Scopes statt einer Rolle und kann sich nie in dieser Anwendung anmelden.',
      sections: {
        'Tokens in use': 'Tokens im Einsatz',
        'New token': 'Neuer Token',
        'Copying the secret': 'Secret kopieren',
      },
      contexts: {
        'token-dialog': 'Klicken Sie rechts oben auf „New token“.',
        'token-created': 'Füllen Sie den Dialog „New API token“ aus und klicken Sie auf „Create“.',
      },
    },
    'sr-Latn-RS': {
      title: 'API tokeni',
      intro:
        'Pristupni podaci za pipeline-ove: čitanje objavljenog bundle-a, objavljivanje release-a, prijava ključeva koji nedostaju. Token nosi scope-ove umesto uloge i nikad ne može da se prijavi u ovu aplikaciju.',
      sections: {
        'Tokens in use': 'Tokeni u upotrebi',
        'New token': 'Novi token',
        'Copying the secret': 'Kopiranje tajne vrednosti',
      },
      contexts: {
        'token-dialog': 'Kliknite na „New token“ gore desno.',
        'token-created': 'Popunite dijalog „New API token“ i kliknite na „Create“.',
      },
    },
  },
};
